import React, { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import { colors } from 'constants/colors';
import { EnumNotification } from 'constants/enums';
import { INotification } from './reducer';
import { getNotificationsList } from './selectors';
import { removeNotificationAction } from './actions';
import styled from 'styled-components';
import { mediaSmDown, mediaSmUp } from '../../theme/helpers/css';
import { FontSize } from '../../theme';
import Text from 'Components/Typography/Text';
import Flex from 'Components/Grid/Flex';
import Box from 'Components/Grid/Box';
import { TransformTransitionGroup } from '../../Components/Animation';

interface INotificationItem {
  type: EnumNotification;
}

const ThemeBackgroundColors = {
  [EnumNotification.success]: colors.grederlevy,
  [EnumNotification.danger]: colors.watermelon,
  [EnumNotification.warning]: colors.maize,
  [EnumNotification.info]: colors.watermelon,
};

const ThemeColors = {
  [EnumNotification.success]: colors.darkBlueGreen,
  [EnumNotification.danger]: colors.white,
  [EnumNotification.warning]: colors.darkBlueGreen,
  [EnumNotification.info]: colors.white,
};

const NotificationItem = styled(Flex).attrs({
  middle: true,
})<INotificationItem>`
  background: ${props => ThemeBackgroundColors[props.type]};
  color: ${props => ThemeColors[props.type]};
  padding: 12px 17px;
  ${mediaSmUp`
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    border-bottom-left-radius: 10px;
    margin-top: 20px;
  `}
`;

const NotificationsContainer = styled(TransformTransitionGroup)`
  z-index: 10000;
  position: fixed;
  max-height: 100%;
  ${mediaSmUp`
    right: 20px;
    bottom: 20px;
  `}
  ${mediaSmDown`
    top: 0;
    left: 0;
    right: 0;
  `}
`;

const Icon = styled(Box).attrs({
  ml: 2,
})<{ src: string }>`
  width: 24px;
  height: 24px;
  display: inline-block;
  background-size: contain;
  background-repeat: no-repeat;
  background-image: url(${({ src }) => src});
  ${mediaSmDown`
    width: 18px;
    height: 18px;
  `}
`;

export const NotificationModuleContainer = () => {
  const dispatch = useDispatch();
  const notifications = useSelector(getNotificationsList);

  const removeNotification = useCallback(() => dispatch(removeNotificationAction()), [dispatch]);

  useEffect(() => {
    notifications.forEach(({ timeout }) => {
      setTimeout(() => {
        removeNotification();
      }, timeout || 2000);
    });
  }, [notifications, removeNotification]);

  return (
    <NotificationsContainer $transitionName="fade">
      {notifications.map(({ id, theme, text, icon }: INotification) => (
        <CSSTransition key={id} classNames="fade" timeout={200}>
          <NotificationItem type={theme}>
            <Text size={FontSize.m}>{text}</Text>
            {icon && <Icon src={icon} />}
          </NotificationItem>
        </CSSTransition>
      ))}
    </NotificationsContainer>
  );
};

export default NotificationModuleContainer;
